import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AsYouType, getExampleNumber } from 'libphonenumber-js/min';
import type { CountryCode } from 'libphonenumber-js/min';
import examples from 'libphonenumber-js/examples.mobile.json';
import {
  COUNTRIES,
  POPULAR_ISO2,
  type Country,
  countryName,
  findCountryByDialPrefix,
  findCountryByIso2,
  flagEmoji,
  isKnownCountry,
} from '../lib/countries';
import { resolveInitialCountry, saveCountryPreference } from '../lib/resolveCountry';

// Flag + dial-code chip that opens a searchable dropdown, paired with
// a national-number input. Emits an E.164-shaped string ("+34612345678")
// through `onChange` on every keystroke so form state stays in sync,
// but does not enforce country-specific length/shape — that's the job
// of libphonenumber-js validation at submit time (Phase 3).
//
// Paste behaviour: when the user pastes a string starting with `+`,
// the longest-prefix match flips the country picker automatically and
// the remaining digits populate the national-number field. Lets users
// paste full international numbers from contact cards without having
// to manually match the flag.
export interface PhoneInputProps {
  value: string;
  onChange: (e164: string) => void;
  placeholder?: string;
  required?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  id?: string;
  // Class applied to the visible outer wrapper. Defaults match the
  // landing / channels / auth pages — override to tighten vertical
  // padding in denser forms.
  className?: string;
}

export function PhoneInput({
  value,
  onChange,
  placeholder,
  required,
  autoFocus,
  disabled,
  id,
  className,
}: PhoneInputProps) {
  const { t, i18n } = useTranslation('common');

  // Country selection state. `country` is always a valid entry from
  // COUNTRIES — we never hold onto a bare ISO-2 string.
  const [country, setCountry] = useState<Country>(() => {
    const fromValue = value && value.startsWith('+') ? findCountryByDialPrefix(value) : null;
    if (fromValue) return fromValue;
    const iso2 = resolveInitialCountry();
    return findCountryByIso2(iso2) ?? findCountryByIso2('US')!;
  });

  // National number (no leading `+` or dial code) — the string the
  // user types into the visible text field. Stored formatted (with
  // AsYouType separators matching the country's conventions) so what
  // the user sees IS what they typed; `emit` strips separators before
  // handing off the canonical E.164 to the parent.
  const [national, setNational] = useState<string>(() =>
    formatNational(extractNational(value, country.dial), country.iso2),
  );

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const nationalRef = useRef<HTMLInputElement>(null);

  // If the parent re-hydrates `value` (e.g. pre-filled from URL
  // params on AuthPage), update local state to match. The previous
  // version only ran when the *country* changed — which silently
  // failed for the most common case: a user whose geo-resolved
  // country (e.g. ES from CloudFront) matched the URL-param dial
  // code (+34). Country guard passed, national digits were never
  // populated, the input rendered blank — forcing the user to re-
  // type. Now we sync the national digits whenever `value` doesn't
  // already round-trip to the same digit string we currently hold.
  useEffect(() => {
    if (!value) return;
    if (!value.startsWith('+')) return;
    const matched = findCountryByDialPrefix(value);
    if (!matched) return;
    const incomingDigits = extractNational(value, matched.dial).replace(/\D/g, '');
    const currentDigits = national.replace(/\D/g, '');
    if (matched.iso2 === country.iso2 && incomingDigits === currentDigits) return;
    setCountry(matched);
    setNational(formatNational(incomingDigits, matched.iso2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Close the dropdown when clicking outside.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Focus the search box when the dropdown opens.
  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 10);
    else setQuery('');
  }, [open]);

  const emit = (nextCountry: Country, nextNational: string) => {
    const digits = nextNational.replace(/\D/g, '');
    onChange(digits ? `+${nextCountry.dial}${digits}` : '');
  };

  const handleCountryPick = (c: Country) => {
    setCountry(c);
    setOpen(false);
    saveCountryPreference(c.iso2);
    // Reformat the existing national number under the new country's
    // AsYouType rules (e.g. switching ES→US reshapes '612345678' →
    // '(612) 345-678'). Digits are preserved; only separators change.
    const reformatted = formatNational(national, c.iso2);
    setNational(reformatted);
    emit(c, reformatted);
    // Return focus to the number input so the user can keep typing.
    setTimeout(() => nationalRef.current?.focus(), 10);
  };

  const handleNationalChange = (raw: string) => {
    // Paste-with-plus path: if the user pastes a full E.164 we re-parse
    // both the country and the digits. Done here (not in an onPaste
    // handler) so programmatic fills also flow through.
    if (raw.startsWith('+')) {
      const matched = findCountryByDialPrefix(raw);
      if (matched) {
        const rest = raw.slice(1).replace(/\D/g, '').slice(matched.dial.length);
        const formatted = formatNational(rest, matched.iso2);
        setCountry(matched);
        setNational(formatted);
        saveCountryPreference(matched.iso2);
        emit(matched, formatted);
        return;
      }
    }
    // Normal typing: keep only digits (AsYouType handles spaces/dashes
    // itself based on the country's format rules — letting the user's
    // own whitespace through confuses the formatter's state machine).
    const digits = raw.replace(/\D/g, '');
    const formatted = formatNational(digits, country.iso2);
    setNational(formatted);
    emit(country, formatted);
  };

  // Country-specific placeholder via libphonenumber's example number
  // table. Prefers the caller-supplied `placeholder` prop as the
  // ultimate fallback for territories without an example (rare).
  const dynamicPlaceholder = useMemo(() => {
    try {
      const example = getExampleNumber(country.iso2 as CountryCode, examples);
      if (example) return example.formatNational();
    } catch {
      /* unsupported country code — fall through */
    }
    return placeholder ?? '';
  }, [country.iso2, placeholder]);

  const filtered = useMemo(() => filterCountries(COUNTRIES, query, i18n.language), [query, i18n.language]);
  const grouped = useMemo(() => groupCountries(filtered, query, i18n.language), [filtered, query, i18n.language]);

  return (
    <div ref={wrapperRef} className={`relative flex items-stretch ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('phoneInput.pickCountry', {
          defaultValue: 'Choose country for your phone number',
        })}
        className={`flex items-center gap-1.5 rounded-l-lg border border-r-0 border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed ${open ? 'ring-1 ring-primary-500 border-primary-500' : ''}`}
      >
        <span className="text-base leading-none" aria-hidden="true">{flagEmoji(country.iso2) || country.iso2}</span>
        <span className="font-medium text-gray-900">+{country.dial}</span>
        <svg className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z" clipRule="evenodd" />
        </svg>
      </button>
      <input
        ref={nationalRef}
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        value={national}
        onChange={(e) => handleNationalChange(e.target.value)}
        placeholder={dynamicPlaceholder}
        required={required}
        autoFocus={autoFocus}
        disabled={disabled}
        className="flex-1 min-w-0 rounded-r-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
      />

      {open && (
        <div
          role="listbox"
          className="absolute left-0 top-full z-50 mt-1 w-full min-w-[320px] max-h-80 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          <div className="sticky top-0 border-b border-gray-100 bg-white p-2">
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('phoneInput.searchPlaceholder', {
                defaultValue: 'Search country or +code…',
              })}
              className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
          </div>
          {grouped.length === 0 && (
            <p className="p-4 text-center text-sm text-gray-400">
              {t('phoneInput.noResults', { defaultValue: 'No countries match.' })}
            </p>
          )}
          {grouped.map(({ label, items }) => (
            <div key={label}>
              {label && (
                <p className="sticky top-[46px] border-b border-gray-100 bg-gray-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                  {label === 'popular'
                    ? t('phoneInput.groupPopular', { defaultValue: 'Popular' })
                    : t('phoneInput.groupAll', { defaultValue: 'All countries' })}
                </p>
              )}
              {items.map((c) => (
                <button
                  key={c.iso2}
                  type="button"
                  onClick={() => handleCountryPick(c)}
                  role="option"
                  aria-selected={c.iso2 === country.iso2}
                  className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50 ${c.iso2 === country.iso2 ? 'bg-primary-50 text-primary-700' : 'text-gray-700'}`}
                >
                  <span className="text-lg leading-none" aria-hidden="true">{flagEmoji(c.iso2) || c.iso2}</span>
                  <span className="flex-1 truncate">{countryName(c.iso2, i18n.language)}</span>
                  <span className="text-xs text-gray-500">+{c.dial}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Given an E.164 value and the expected dial code, return the
// national portion (digits only). Used to seed the visible text
// field from a prefilled value.
function extractNational(e164: string, dial: string): string {
  if (!e164 || !e164.startsWith('+')) return e164.replace(/[^\d\s\-().]/g, '');
  const digits = e164.slice(1).replace(/\D/g, '');
  if (digits.startsWith(dial)) return digits.slice(dial.length);
  return digits;
}

// Format a national digit string using libphonenumber's AsYouType for
// the given country ("612345678" + ES → "612 34 56 78"). On unknown
// / unsupported countries we return the raw digits — better to keep
// the user's input visible than swallow it.
function formatNational(raw: string, iso2: string): string {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  try {
    const formatter = new AsYouType(iso2 as CountryCode);
    const out = formatter.input(digits);
    return out || digits;
  } catch {
    return digits;
  }
}

function filterCountries(all: readonly Country[], rawQuery: string, locale: string): Country[] {
  const q = rawQuery.trim().toLowerCase();
  if (!q) return [...all];

  // Digits-only search matches dial codes (with or without leading `+`).
  const digits = q.replace(/\D/g, '');
  if (digits && /^\+?\d+$/.test(q)) {
    return all.filter((c) => c.dial.startsWith(digits) || digits.startsWith(c.dial));
  }

  return all.filter((c) => {
    const localName = countryName(c.iso2, locale).toLowerCase();
    return (
      localName.includes(q)
      || c.name.toLowerCase().includes(q)
      || c.iso2.toLowerCase().includes(q)
      || c.dial.startsWith(q)
    );
  });
}

interface Group {
  label: 'popular' | 'all' | '';
  items: Country[];
}

function groupCountries(list: readonly Country[], rawQuery: string, locale: string): Group[] {
  // While searching, skip the popular pinning — the user is already
  // narrowing by name.
  if (rawQuery.trim()) {
    const sorted = [...list].sort((a, b) =>
      countryName(a.iso2, locale).localeCompare(countryName(b.iso2, locale)),
    );
    return [{ label: '', items: sorted }];
  }

  const popular = list.filter((c) => POPULAR_ISO2.has(c.iso2));
  const rest = list
    .filter((c) => !POPULAR_ISO2.has(c.iso2))
    .sort((a, b) => countryName(a.iso2, locale).localeCompare(countryName(b.iso2, locale)));

  const groups: Group[] = [];
  if (popular.length) {
    // Preserve the curated POPULAR_ISO2 order — important because
    // POPULAR_ISO2 is intentionally ordered by expected signup volume.
    const popularOrdered = [...POPULAR_ISO2]
      .map((iso) => popular.find((c) => c.iso2 === iso))
      .filter((c): c is Country => !!c && isKnownCountry(c.iso2));
    groups.push({ label: 'popular', items: popularOrdered });
  }
  groups.push({ label: 'all', items: rest });
  return groups;
}
